import React, { useState, useEffect, useMemo } from "react";
import { ChevronDown } from 'lucide-react';
import { useLoading } from "../../../contexts/LoadingContext";
import apiClient from "../../../utils/apiClient";
import { ReactComponent as Logo } from "../../../assets/images/popashot_logo.svg";
import ErrorMessage from "../../common/ErrorMessage";
import LoadingButton from "../../common/LoadingButton";

const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 110 }, (_, i) => currentYear - i);

const BirthDateStep = ({ email, onSuccess }) => {
    const [month, setMonth] = useState("");
    const [day, setDay] = useState("");
    const [year, setYear] = useState("");
    const [error, setError] = useState("");
    const { startLoading, stopLoading } = useLoading();

    // Show maximum possible days for each month, let Django validate
    const daysInMonth = useMemo(() => {
        if (!month) return 31;
        
        const maxDaysPerMonth = {
            1: 31,  // January
            2: 29,  // February (max possible)
            3: 31,  // March
            4: 30,  // April
            5: 31,  // May
            6: 30,  // June
            7: 31,  // July
            8: 31,  // August
            9: 30,  // September
            10: 31, // October
            11: 30, // November
            12: 31  // December
        };
        
        return maxDaysPerMonth[parseInt(month)] || 31;
    }, [month]);

    // Clear day selection if it becomes invalid for the selected month
    useEffect(() => {
        if (day && day > daysInMonth) {
            setDay("");
        }
    }, [daysInMonth, day]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!month || !day || !year) {
            setError("Date of birth is required");
            return;
        }

        // Build ISO date string YYYY-MM-DD
        const dateOfBirth = `${year}-${month.toString().padStart(2, "0")}-${day
            .toString()
            .padStart(2, "0")}`;

        startLoading("birthDate");
        try {
            await apiClient.patch("/api/users/profile/", {
                date_of_birth: dateOfBirth
            });

            onSuccess();
        } catch (error) {
            console.error("Birth date submission error:", error);
            const errorData = error.response?.data;
            if (errorData) {
                if (errorData.date_of_birth) {
                    setError(errorData.date_of_birth[0]);
                } else if (errorData.detail) {
                    setError(errorData.detail);
                } else {
                    setError("Failed to save date of birth. Please try again.");
                }
            } else {
                setError(error.message || "An error occurred. Please try again.");
            }
        } finally {
            stopLoading("birthDate");
        }
    };

    return (
        <div className="space-y-8 bg-transparent">
            <div className="flex items-center justify-center">
                <Logo className="w-48 h-auto" />
            </div>

            <div className="text-center">
                <h1>Enter your Birthdate</h1>
                <p className="mt-4">You're signing up as {email}.</p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                <div className="flex gap-2">
                    {/* Month */}
                    <div className="relative w-full">
                        <select
                            id="month"
                            name="month"
                            aria-label="Month"
                            required
                            className="appearance-none w-full px-3 py-3 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                        >
                            <option value="">Month</option>
                            {months.map((m) => (
                                <option key={m.value} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 pointer-events-none text-gray-400" />
                    </div>

                    {/* Day */}
                    <div className="relative w-full">
                        <select
                            id="day"
                            name="day"
                            aria-label="Day"
                            required
                            className="appearance-none w-full px-3 py-3 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={day}
                            onChange={(e) => setDay(e.target.value)}
                        >
                            <option value="">Day</option>
                            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                                <option key={d} value={d}>
                                    {d}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 pointer-events-none text-gray-400" />
                    </div>

                    {/* Year */}
                    <div className="relative w-full">
                        <select
                            id="year"
                            name="year"
                            aria-label="Year"
                            required
                            className="appearance-none w-full px-3 py-3 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                        >
                            <option value="">Year</option>
                            {years.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 pointer-events-none text-gray-400" />
                    </div>
                </div>

                <ErrorMessage error={error} />

                <LoadingButton
                    type="submit"
                    className="w-full btn-primary"
                    loadingKey="birthDate"
                >
                    Continue
                </LoadingButton>

                <div className="text-center">
                    <p className="text-sm">You're signing up as {email}.</p>
                    <p className="text-sm">
                        Wrong account? {" "}
                        <button
                            type="button"
                            onClick={() => (window.location.href = "/login")}
                            className="text-popashot-blue-light hover:text-popashot-blue"
                        >
                            Sign in
                        </button>{" "}
                        instead.
                    </p>
                </div>
            </form>
        </div>
    );
};

export default BirthDateStep;
